import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import Form from './components/Form';
import Header from './components/Header';
import Login from './components/Login';
import axios from 'axios';

// Special component to handle the /home route which serves dual purpose
function HomeRouteHandler({ setUserData, authorizedIds }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  
  useEffect(() => {
    // Check if this is an SSO callback (has a code parameter)
    const query = new URLSearchParams(location.search);
    const code = query.get("code");
  
    if (code && !processing) {
      setProcessing(true);
      
      // Process the SSO code
      const processCode = async () => {
        try {
          const tokenUrl = "https://sso.thomsonreuters.com/as/token.oauth2";
          const data = new URLSearchParams({
            grant_type: "authorization_code",
            client_id: process.env.REACT_APP_CLIENT_ID,
            client_secret: process.env.REACT_APP_CLIENT_SECRET,
            code: code,
            redirect_uri: process.env.REACT_APP_REDIRECT_URI,
          });

          const tokenResponse = await axios.post(tokenUrl, data, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });

          if (tokenResponse.data && tokenResponse.data.access_token) {
            // Get user profile
            const profileUrl = "https://sso.thomsonreuters.com/idp/userinfo.openid";
            const profileData = new URLSearchParams({
              schema: "openid",
              scope: "openid email profile",
              access_token: tokenResponse.data.access_token,
            });

            const profileResponse = await axios.post(profileUrl, profileData, {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
            });

            if (profileResponse.data) {
              const userData = {
                id: profileResponse.data.sub,
                email: profileResponse.data.mail,
                firstName: profileResponse.data.firstname || profileResponse.data.legalname,
                lastName: profileResponse.data.lastname || profileResponse.data.sn,
              };

              // Store user data
              localStorage.setItem("userData", JSON.stringify(userData));
              setUserData(userData);
              
              // Clean up URL to prevent processing the code again on refresh
              window.history.replaceState({}, document.title, "/home");
              
              // Force a re-render of the component
              setProcessing(false);
              
              // If there was an original path stored, navigate to it
              const originalPath = sessionStorage.getItem("originalPath");
              if (originalPath && originalPath !== "/home") {
                sessionStorage.removeItem("originalPath");
                navigate(originalPath);
              }
            } else {
              navigate('/login');
            }
          } else {
            navigate('/login');
          }
        } catch (error) {
          console.error("SSO Processing Error:", error);
          navigate('/login');
        }
      };
      
      processCode();
    } else if (!code) {
      // If no code and no user data, redirect to login
      const userData = localStorage.getItem("userData");
      if (!userData) {
        sessionStorage.setItem("originalPath", location.pathname);
        navigate('/login');
      }
    }
  }, [location, navigate, setUserData, processing]);

  if (processing) {
    return <div>Please wait...</div>;
  }
  
  // This will only show briefly before the effect redirects
  return <div>Please wait.....</div>;
}

function App() {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true); // Added for auth loading state
  const [authorizedIds, setAuthorizedIds] = useState([]);

  useEffect(() => {
    // Fetch authorized employee IDs from the endpoint
    const fetchAuthorizedIds = async () => {
      setAuthLoading(true); // Start loading auth data
      try {
        const baseEndpoint = process.env.REACT_APP_USERS_ID_ENDPOINT;
        // console.log("Fetching authorized IDs from endpoint:", baseEndpoint);
        
        const response = await axios.post(baseEndpoint);
        // console.log("Response from endpoint:", response.data);
        
        if (response.data && response.data.emails) {
          // Convert all IDs to strings for consistent comparison
          const ids = response.data.emails.map(id => String(id));
          setAuthorizedIds(ids);
          // console.log("Authorized IDs set:", ids);
        } else {
          console.error("Invalid response format - missing emails array:", response.data);
        }
      } catch (error) {
        console.error("Error fetching authorized IDs:", error);
      } finally {
        setAuthLoading(false); // End loading regardless of success/failure
      }
    };

    fetchAuthorizedIds();
    
    // Check localStorage for user data on initial load
    const storedUserData = localStorage.getItem("userData");
    if (storedUserData) {
      try {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
        // console.log("User data loaded from localStorage:", parsedUserData);
      } catch (e) {
        console.error("Error parsing stored user data", e);
        localStorage.removeItem("userData");
      }
    }
    setIsLoading(false);
  }, []);

  // Create an unauthorized access component for reuse
  const UnauthorizedAccess = () => {
    const navigate = useNavigate();
    
    // const handleLogout = () => {
    //   localStorage.removeItem("userData");
    //   setUserData(null);
    //   navigate('/login');
    // };
    
    return (
      <div className="unauthorized-message" style={{textAlign: 'center', padding: '50px 20px'}}>
        <h2>Unauthorized Access</h2>
        <p>You don't have permission to access this application.</p>
        {/* <button 
          onClick={handleLogout}
          style={{
            padding: '10px 20px',
            background: '#d9534f',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          Logout
        </button> */}
      </div>
    );
  };

  // Check if user is authorized
  const isUserAuthorized = () => {
    if (userData && authorizedIds.length > 0) {
      const isAuthorized = authorizedIds.includes(String(userData.id));
      // console.log(`User ID ${userData.id} authorization check: ${isAuthorized}`);
      // console.log("Current authorized IDs:", authorizedIds);
      return isAuthorized;
    }
    return false;
  };

  // Create a protected route component with ID verification
  const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    
    if (isLoading || authLoading) return <div>Loading...</div>;
    
    if (!userData) {
      // Save the current path before redirecting
      sessionStorage.setItem("originalPath", location.pathname);
      return <Navigate to="/login" />;
    }
    
    // Check if the user has an authorized ID
    if (!isUserAuthorized()) {
      return <UnauthorizedAccess />;
    }
    
    return children;
  };

  // Determine whether to show header based on route and auth status
  const shouldShowHeader = () => {
    // Don't show header on login page or when showing unauthorized access
    return userData && !authLoading && isUserAuthorized();
  };

  return (
    <Router>
      <div>
        {/* Only show header when user is logged in AND authorized */}
        {shouldShowHeader() && <Header userData={userData}/>}
        
        <div className="App-content">
          <h1 style={{textAlign:"center"}}>Express Platform AI Provisioning</h1>
          <Routes>
            <Route path="/" element={<Navigate to="/home" />} />
            <Route 
              path="/login" 
              element={
                userData ? 
                <Navigate to={sessionStorage.getItem("originalPath") || "/home"} /> : 
                <Login setUserData={setUserData} />
              } 
            />
            <Route
              path="/home"
              element={
                userData ? (
                  authLoading ? (
                    <div>Please wait...</div>
                  ) : isUserAuthorized() ? (
                    <Form
                      userData={userData}
                      setUserData={setUserData}
                    />
                  ) : (
                    <UnauthorizedAccess />
                  )
                ) : (
                  // Special handling for /home since it's both a protected route AND the SSO callback
                  <HomeRouteHandler setUserData={setUserData} authorizedIds={authorizedIds} />
                )
              }
            />
            {/* Add routes for other protected paths */}
            <Route 
              path="*" 
              element={
                <ProtectedRoute>
                  <Navigate to="/home" />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
